<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const COST_PER_1K_TOKENS = 0.015;

const props = defineProps({
  monthlyTokenLimit: { type: [Number, null], default: null },
  tokensUsed: { type: Number, default: 0 },
});

const emit = defineEmits(['update:monthlyTokenLimit']);

const { t } = useI18n();

const hasLimit = computed(
  () => props.monthlyTokenLimit !== null && props.monthlyTokenLimit > 0
);

const usagePercent = computed(() => {
  if (!hasLimit.value) return 0;
  return Math.min(
    100,
    Math.round((props.tokensUsed / props.monthlyTokenLimit) * 100)
  );
});

const barColor = computed(() => {
  const pct = usagePercent.value;
  if (pct < 50) return 'bg-n-teal-9';
  if (pct < 80) return 'bg-n-amber-9';
  return 'bg-n-ruby-9';
});

const estimatedCost = computed(() => {
  return ((props.tokensUsed / 1000) * COST_PER_1K_TOKENS).toFixed(2);
});

const formatNumber = num => {
  return new Intl.NumberFormat().format(num);
};

const handleLimitChange = event => {
  const val = parseInt(event.target.value, 10);
  emit('update:monthlyTokenLimit', Number.isNaN(val) || val <= 0 ? null : val);
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <!-- Token limit input -->
    <div class="flex flex-col gap-1">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAPTAIN_SETTINGS.USAGE_LIMITS.MONTHLY_LIMIT') }}
      </label>
      <input
        type="number"
        :value="monthlyTokenLimit || ''"
        :placeholder="t('CAPTAIN_SETTINGS.USAGE_LIMITS.MONTHLY_LIMIT_HELP')"
        min="0"
        class="block w-full h-10 px-3 py-2 text-sm rounded-lg border-none outline outline-1 outline-offset-[-1px] outline-n-weak bg-n-alpha-black2 text-n-slate-12 hover:outline-n-slate-6 focus:outline-n-blue-9 placeholder:text-n-slate-10 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        @input="handleLimitChange"
      />
      <p class="text-xs text-n-slate-11 mb-0">
        {{ t('CAPTAIN_SETTINGS.USAGE_LIMITS.MONTHLY_LIMIT_HELP') }}
      </p>
    </div>

    <!-- Progress bar -->
    <div v-if="hasLimit" class="flex flex-col gap-2">
      <div class="w-full h-2.5 rounded-full bg-n-alpha-black2 overflow-hidden">
        <div
          class="h-full rounded-full transition-all duration-300"
          :class="barColor"
          :style="{ width: `${usagePercent}%` }"
        />
      </div>
      <div class="flex justify-between text-xs text-n-slate-11">
        <span>
          {{
            t('CAPTAIN_SETTINGS.USAGE_LIMITS.USAGE_TEXT', {
              used: formatNumber(tokensUsed),
              limit: formatNumber(monthlyTokenLimit),
              percent: usagePercent,
            })
          }}
        </span>
        <span>
          {{
            t('CAPTAIN_SETTINGS.USAGE_LIMITS.ESTIMATED_COST', {
              cost: estimatedCost,
            })
          }}
        </span>
      </div>
    </div>

    <div v-else class="text-sm text-n-slate-11">
      {{ t('CAPTAIN_SETTINGS.USAGE_LIMITS.NO_LIMIT') }}
    </div>
  </div>
</template>
